import React from "react";
import { useNavigate } from "react-router-dom";
import { GoogleIcon, Spinner } from "@/components/peeps/ui";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { CustomSections } from "@/components/peeps/CustomSections";
import logo from "@/assets/peeps-logo.png";

function useAuthError() {
  const p = new URLSearchParams(window.location.search);
  const err = p.get("auth_error");
  if (!err) return null;
  const map = {
    token: "Google rejected the sign-in token exchange.",
    consent: "Sign-in was cancelled at Google.",
    state: "Your sign-in session expired. Please try again.",
    userinfo: "Couldn't read your Google profile.",
    network: "Couldn't reach Google. Please try again.",
    server: "Something went wrong creating your session.",
    nocode: "Google didn't return an authorization code.",
  };
  return {
    title: map[err] || `Sign-in error: ${err}`,
    google: p.get("g"),
    status: p.get("code"),
    redirectUri: p.get("ru"),
  };
}

export default function Login() {
  const { login, signingIn, user } = useAuth();
  const authErr = useAuthError();
  const navigate = useNavigate();
  const isAdmin = !!user?.is_app_admin;

  return (
    <div className="min-h-screen flex flex-col justify-between px-6 py-12 max-w-md mx-auto" style={{ background: colors.surface }}>
      <div className="mt-12">
        <img src={logo} alt="peeps" className="w-20 h-20 rounded-3xl mb-4" />
        <p className="font-display font-bold text-[22px] mb-8" style={{ color: colors.brand }}>peeps</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl leading-tight" style={{ color: colors.onSurface }}>
          Plan things with your people.
        </h1>
        <p className="font-display text-[15px] mt-3 leading-relaxed" style={{ color: colors.onSurfaceTertiary }}>
          Invite-only groups, shared calendars, and every plan in one place.
        </p>
        <div className="flex flex-col items-start gap-1.5 mt-4">
          <button
            data-testid="login-about-link"
            onClick={() => navigate("/about")}
            className="font-display font-medium text-[14px] underline underline-offset-4"
            style={{ color: colors.brandSecondary }}
          >
            What is peeps? →
          </button>
          <button
            data-testid="login-guide-link"
            onClick={() => navigate("/how-it-works")}
            className="font-display font-medium text-[14px] underline underline-offset-4"
            style={{ color: colors.brandSecondary }}
          >
            How peeps works →
          </button>
        </div>

        {authErr && (
          <div data-testid="auth-error-banner" className="mt-6 rounded-xl p-4 text-[13px] font-display" style={{ background: "#2A1518", border: "1px solid #5A2A2E", color: "#FCA5A5" }}>
            <p className="font-bold mb-1">Couldn’t sign you in</p>
            <p className="mb-2" style={{ color: "#F8B4B4" }}>{authErr.title}</p>
            {(authErr.google || authErr.status || authErr.redirectUri) && (
              <div className="text-[11px] leading-relaxed" style={{ color: "#E88" }}>
                {authErr.google && <div>google_error: <b>{authErr.google}</b></div>}
                {authErr.status && <div>http_status: <b>{authErr.status}</b></div>}
                {authErr.redirectUri && <div className="break-all">redirect_uri: <b>{authErr.redirectUri}</b></div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom HTML sections (e.g. the hero explainer animation) live in the
          space between the intro and the sign-in button. Admins get inline
          add/edit/reorder controls here. */}
      <div className="flex-1 flex flex-col justify-center py-6">
        <CustomSections page="home" />
      </div>

      {isAdmin ? (
        <div className="flex flex-col gap-3">
          <div className="rounded-xl p-3 text-[12.5px] font-display" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, color: colors.onSurfaceTertiary }}>
            Admin preview of the landing page — changes above are live for visitors.
          </div>
          <button data-testid="login-back-to-app" onClick={() => navigate("/")} className="flex items-center justify-center gap-2.5 rounded-xl py-3.5 font-display font-bold text-[15px]" style={{ background: colors.surfaceTertiary, color: colors.onSurface, border: `1px solid ${colors.border}` }}>
            Back to app
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <button
            data-testid="google-signin-button"
            onClick={() => login("/")}
            disabled={signingIn}
            className="flex items-center justify-center gap-2.5 rounded-xl py-4 font-display font-bold text-[15.5px] transition-transform active:scale-[0.98]"
            style={{ background: colors.surfaceInverse, color: colors.onSurfaceInverse }}
          >
            {signingIn ? <Spinner style={{ borderTopColor: colors.onSurfaceInverse }} /> : (<><GoogleIcon /> Sign in with Google</>)}
          </button>
          <p className="font-display text-xs text-center leading-snug" style={{ color: colors.faint }}>
            By continuing you agree to keep the group chat drama to a minimum.
          </p>
        </div>
      )}
    </div>
  );
}
